import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { Alert, Button, Dropdown, Nav, NavDropdown, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

import GroupVisibleComponent from '../../GroupVisibleComponent';
import { ReactComponent as Logo } from '../../images/FWC-Logo.svg';

import { API } from 'aws-amplify';
import { BILLING_DATA_TAB, CLIENT_METADATA_TAB, COMPANY_DATA_TAB, SURVEY_SUBMISSIONS_TAB, TEMPLATE_DEFINITIONS_TAB, USAGE_REPORT_TAB, CLIENT_DATA_REQUESTS_TAB } from '../../AdminConsole/AdminConsole';
import { useStateStore } from '../../AppState';
import { getAuthToken, USER_GROUPS } from '../../utils/AuthUtils';
import './styles.scss';

const NavBar = ({ signOut, user, landingPagePath = "/survey-data/console" }) => {
	const { loading, setLoadingStatus } = useStateStore();
	const { pathname } = useLocation();
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [meritPayError, setMeritPayError] = useState("");
	const isMobile = useMediaQuery({ maxWidth: 768 });
	const downloadLinkRef = useRef(null);
	const navigate = useNavigate();

	const getRelevantDoc = async (docType) => {
		setLoadingStatus(true);
		const url = '/relevant-documents?docType=' + docType;
		const authToken = await getAuthToken();
		API.get('AnnualCompensations', url, {
			headers: {
				'Token': authToken
			}
		}).then((response) => {
			setLoadingStatus(false);
			if (downloadLinkRef.current) {
				downloadLinkRef.current.href = response.url;
				downloadLinkRef.current.click();
			}
		}).catch((error) => {
			setLoadingStatus(false);
			if (error !== undefined && error.response !== undefined &&
				error.response.data !== undefined && error.response.data.message !== undefined) {
				console.log(error.response.data.message);
			} else {
				console.log("An error occurred while retrieving compensation data. Please try again later.");
			}
		});
	}

	const toggleMobileMenu = () => {
		setIsMobileMenuOpen(!isMobileMenuOpen);
	}

	const retrieveMeritPayLaunchUrl = async () => {
		const authToken = await getAuthToken();
		const response = await API.get("AnnualCompensations", "/merit-pay/login", {
			headers: { "Token": authToken }
		});
		return response.url;
	}

	const handleLaunchMeritPay = async (event) => {
		event.preventDefault();
		event.stopPropagation();
		setMeritPayError("");
		if (isMobile) setIsMobileMenuOpen(false);

		const launchWindow = window.open("about:blank", "_blank");
		if (launchWindow) {
			launchWindow.opener = null;
			launchWindow.document.title = "Opening Merit Pay";
			launchWindow.document.body.textContent = "Opening Merit Pay...";
		}

		try {
			const launchUrl = await retrieveMeritPayLaunchUrl();
			if (!launchUrl) {
				throw new Error("Merit Pay did not return a launch URL.");
			}

			if (!launchWindow) {
				window.location.assign(launchUrl);
				return;
			}

			if (launchWindow.closed) {
				throw new Error("The Merit Pay window was closed before it finished loading.");
			}

			launchWindow.location.replace(launchUrl);
		} catch (error) {
			if (launchWindow && !launchWindow.closed) {
				launchWindow.close();
			}
			console.error("Unable to launch Merit Pay.", error);
			setMeritPayError("Unable to launch Merit Pay. Please try again.");
		}
	}

	const renderNavigationItems = () => (
		<Nav className='main-navbar-actions' activeKey="/" onSelect={(selectedKey) => {
			navigate(selectedKey);
			if (isMobile) setIsMobileMenuOpen(false);
		}}>
			<GroupVisibleComponent groups={[USER_GROUPS.ADMIN, USER_GROUPS.INTERNAL, USER_GROUPS.CLIENT, USER_GROUPS.EXTERNAL]}>
				<Nav.Item>
					<Nav.Link
						active={pathname.includes("/survey-data")}
						eventKey="/survey-data/console"
					>
						Survey
					</Nav.Link>
				</Nav.Item>
			</GroupVisibleComponent>
			<GroupVisibleComponent groups={["Internal", "Client", "FWC_Admin", "External"]}>
				<NavDropdown title="Dashboards" active={pathname.includes("/dashboard")} id="dashboard-data-dropdown">
					<Dropdown.Header className="dashboard-dropdown-header">Proxy Dashboards</Dropdown.Header>
					<Dropdown.Item eventKey="/dashboard/exec/index">Index Overview</Dropdown.Item>
					<Dropdown.Item eventKey="/dashboard/exec/peer">Peer Breakdown</Dropdown.Item>
					<Dropdown.Header className="dashboard-dropdown-header">Survey Dashboards</Dropdown.Header>
					<Dropdown.Item eventKey="/dashboard/survey-output">Survey Benchmark</Dropdown.Item>
					<Dropdown.Item eventKey="/dashboard/revenue-regression">Revenue Regression</Dropdown.Item>
					<Dropdown.Item eventKey="/dashboard/market-cap-regression">Market Cap Regression</Dropdown.Item>
				</NavDropdown>
			</GroupVisibleComponent>
			<Nav.Item>
				<Nav.Link
					active={pathname === "/"}
					eventKey="/"
				>
					Public Pay Data
				</Nav.Link>
			</Nav.Item>
			<GroupVisibleComponent groups={[USER_GROUPS.INTERNAL]}>
				<NavDropdown
					title="Documents"
					active={pathname.includes("/relevant-documents/")}
					id="documents-dropdown"
				>
					<Dropdown.Item
						key="research-methodology"
						onClick={() => getRelevantDoc("methodology")}
					>
						Methodology
					</Dropdown.Item>
					<Dropdown.Item
						key="ipo-data-overview"
						onClick={() => getRelevantDoc("ipo")}
					>
						IPO Data Overview
					</Dropdown.Item>
					<Dropdown.Item
						key="data-pricing-overview"
						onClick={() => getRelevantDoc("pricing")}
					>
						Data Pricing Overview
					</Dropdown.Item>
					<a href="#self" ref={downloadLinkRef} download id="download-relevant-doc" hidden={true}>Download</a>
				</NavDropdown>
			</GroupVisibleComponent>
			<GroupVisibleComponent groups={[USER_GROUPS.ADMIN, USER_GROUPS.SALES]}>
				<NavDropdown title="Admin" active={pathname.includes("/admin/")} id="admin-dropdown">
					<GroupVisibleComponent groups={[USER_GROUPS.ADMIN]}>
						<Dropdown.Item eventKey={`/admin/${USAGE_REPORT_TAB}`}>Usage Report</Dropdown.Item>
						<Dropdown.Item eventKey={`/admin/${TEMPLATE_DEFINITIONS_TAB}`}>Template Definitions</Dropdown.Item>
						<Dropdown.Item eventKey={`/admin/${SURVEY_SUBMISSIONS_TAB}`}>Survey Submissions</Dropdown.Item>
						<Dropdown.Item eventKey={`/admin/${CLIENT_METADATA_TAB}`}>Client MetaData</Dropdown.Item>
						<Dropdown.Item eventKey={`/admin/${BILLING_DATA_TAB}`}>Billing Data</Dropdown.Item>
						<Dropdown.Item eventKey={`/admin/${COMPANY_DATA_TAB}`}>Company Data</Dropdown.Item>
						<Dropdown.Item eventKey={`/admin/${CLIENT_DATA_REQUESTS_TAB}`}>Client Data Requests</Dropdown.Item>
					</GroupVisibleComponent>
					<GroupVisibleComponent groups={[USER_GROUPS.SALES]}>
						<Dropdown.Item eventKey={`/admin/${SURVEY_SUBMISSIONS_TAB}`}>Survey Submissions</Dropdown.Item>
					</GroupVisibleComponent>
				</NavDropdown>
			</GroupVisibleComponent>

			<GroupVisibleComponent groups={[USER_GROUPS.INTERNAL]}>
				<NavDropdown title="Data Requests" active={pathname.includes("/data-requests")} id="data-requests-dropdown">
					<GroupVisibleComponent groups={['Data_Provider', 'FWC_Admin']}>
						<Dropdown.Item eventKey="/data-requests">All Data Requests</Dropdown.Item>
					</GroupVisibleComponent>
					<Dropdown.Item eventKey="/data-requests/user">My Data Requests</Dropdown.Item>
					<Dropdown.Item eventKey="/data-requests/create">Request Data</Dropdown.Item>
					<Dropdown.Item eventKey="/external-data/options">Options Data</Dropdown.Item>
				</NavDropdown>
			</GroupVisibleComponent>

			<GroupVisibleComponent groups={['Data_Provider', 'FWC_Admin']}>
				<Nav.Item>
					<Nav.Link
						active={pathname.includes("/survey-questionnaire-submission")}
						eventKey="/survey-questionnaire-submission"
					>
						Questionnaire Tracking
					</Nav.Link>
				</Nav.Item>
			</GroupVisibleComponent>
			<GroupVisibleComponent groups={[USER_GROUPS.ADMIN, USER_GROUPS.INTERNAL, USER_GROUPS.EXTERNAL, USER_GROUPS.CLIENT]}>
				<Nav.Item>
					<Nav.Link
						onClick={handleLaunchMeritPay}
					>
						Peer Analysis by MERIT PAY <i className="fa fa-link"></i>
					</Nav.Link>
				</Nav.Item>
			</GroupVisibleComponent>
		</Nav>
	);

	const renderUserActions = () => (
		<div className='user-actions-area'>
			<div className="user-actions">
				<div className="action">
					<GroupVisibleComponent groups={[USER_GROUPS.INTERNAL, USER_GROUPS.CLIENT]}>
						<Button
							variant="secondary"
							onClick={() => navigate("/download-history/user")}
							aria-label="Download History"
						>
							<i className="fa fa-history" title="Downloaded Data History"></i>
						</Button>
					</GroupVisibleComponent>
				</div>
				<div className="action">
					<Button
						variant="secondary"
						onClick={signOut}
						aria-label="Sign Out"
					>
						<i className="fa fa-sign-out-alt" title='Sign Out'></i>
					</Button>
				</div>
			</div>
			<div className='user-name-display'>
				<span>{user?.attributes ? user.attributes.name : ""}</span>
			</div>
		</div>
	);

	return (
		<>
			{meritPayError &&
				<Alert variant="danger" className="status-alert" dismissible onClose={() => setMeritPayError("")}>
					{meritPayError}
				</Alert>
			}
			{loading === true &&
				<div className="loading-backdrop">
					<Spinner animation="border" variant="primary" className="loading-spinner">
						<span className="visually-hidden">Loading...</span>
					</Spinner>
				</div>
			}
			<div className='navbar-container'>
				<nav className="navbar">
					<div className="logo">
						<button
							onClick={() => navigate(landingPagePath)}
							className="logo-button"
							aria-label="Home"
						>
							<Logo title="FW Cook" />
						</button>
					</div>

					{user && (
						<>
							{isMobile && (
								<Button
									variant="secondary"
									onClick={toggleMobileMenu}
									className="mobile-menu-button"
									aria-label="Toggle Menu"
								>
									<i className={`fa fa-${isMobileMenuOpen ? 'times' : 'bars'}`}></i>
								</Button>
							)}

							<div
								className={`navbar-collapse ${isMobileMenuOpen ? 'show' : ''}`}
								id="navbarSupportedContent"
							>
								{renderNavigationItems()}
								{renderUserActions()}
							</div>
						</>
					)}
				</nav>
			</div>
		</>
	);
}

export default NavBar;

NavBar.propTypes = {
	signOut: PropTypes.func,
	user: PropTypes.object,
	landingPagePath: PropTypes.string
};
